import { CardHeader, Skeleton, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { Box } from "@mui/system";

import { useTranslation } from "react-i18next";

import { useAppSelector } from "~/app/store";

import { ProductCard } from "~/components/productCard/ProductCardRootWithSideRibbon";
import { BlindCount } from "~/features/cycleCounts2023/BlindCount";
import { InventoryWithVariantsResponse } from "~/types/api";

import {
  selectIsPreviousBinFlow,
  selectPreviousPutawayTask
} from "./autostorePutaway.slice";

type Props = {
  compartmentData?: InventoryWithVariantsResponse;
  selectedBinNumber?: number;
  selectedCompartment?: number;
};

const CompartmentDataTitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.main
}));

function CompartmentDataCard(props: Props) {
  const { compartmentData, selectedBinNumber, selectedCompartment } = props;
  const { t } = useTranslation();

  const isPreviousBinFlow = useAppSelector(selectIsPreviousBinFlow);
  const previousPutawayTask = useAppSelector(selectPreviousPutawayTask);
  const isPreviousBinData =
    isPreviousBinFlow &&
    previousPutawayTask &&
    previousPutawayTask.binId === selectedBinNumber &&
    previousPutawayTask.compartment === selectedCompartment;

  const { inventory, variant } = isPreviousBinData
    ? {
        inventory: {
          count: previousPutawayTask.quantity
        },
        variant: {
          productName: previousPutawayTask.product.productName,
          upc: previousPutawayTask.product.upc,
          sku: previousPutawayTask.product.sku
        }
      }
    : compartmentData || {};

  const { count } = inventory || {};
  const { productName, sku, upc } = variant || {};
  const selectedCycleCounts = useAppSelector(
    (state) => state.cycleCounts.selectedCycleCounts
  );
  const matchingTask = selectedCycleCounts?.find((cycleCount) =>
    cycleCount.countingTasks.includes(inventory?.inventoryId ?? "")
  );

  return (
    <ProductCard.ProductCardContainer sx={{ p: 2 }}>
      <CardHeader
        title={productName || <Skeleton animation={false} width="80%" />}
        sx={{
          padding: 0,
          marginBottom: "12px",
          fontSize: "20px"
        }}
      />
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(2, 1fr)",
          gap: "40px"
        }}
      >
        <Box>
          <CompartmentDataTitle variant="overline">
            {t("upc")}
          </CompartmentDataTitle>
          <Typography variant="h6" component="h6" sx={{ marginBottom: "8px" }}>
            {upc || <Skeleton animation={false} />}
          </Typography>

          <CompartmentDataTitle variant="overline">
            {t("sku")}
          </CompartmentDataTitle>
          <Typography variant="h6" component="h6">
            {sku || <Skeleton animation={false} />}
          </Typography>
        </Box>

        <Box>
          <CompartmentDataTitle variant="overline">
            {t("quantity")}
          </CompartmentDataTitle>
          <Typography variant="h6" component="h6" sx={{ marginBottom: "8px" }}>
            {count?.value && count.units ? (
              <BlindCount task={matchingTask} count={count} />
            ) : (
              <Skeleton animation={false} />
            )}
          </Typography>
        </Box>
      </Box>
    </ProductCard.ProductCardContainer>
  );
}

export default CompartmentDataCard;
